import { Injectable } from '@angular/core';
import { select } from '@ngneat/elf';
import { withProps } from '@ngneat/elf';
import { Store, createState } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';

// Two independent UI flags drive sidebar behaviour. Keep them distinct:
//   isMobileDrawerOpen — transient, mobile/tablet (<lg) drawer slide-in.
//                        Hamburger toggles it; backdrop/route nav clears it.
//                        Persists as `body.menu-open` for legacy theme CSS in
//                        src/assets/css/style*.css.
//   isSidebarExpanded  — desktop (>=lg) sidebar collapsed/expanded state.
//                        Persists as `body.menu-expanded`.
// The two body-class names are kept for backwards compatibility with vendor
// admin-theme CSS that pre-dates the IGNTR redesign — do not rename them
// without updating ~50 selectors in src/assets/css/style*.css.
export interface UIProps {
  darkMode: boolean;
  isMobileDrawerOpen: boolean;
  isSessionOver: boolean;
  isSidebarExpanded: boolean;
  isInstallPromptDismissed: boolean;
}

const { state, config } = createState(
  withProps<UIProps>({
    darkMode: false,
    isSessionOver: false,
    isMobileDrawerOpen: false,
    isSidebarExpanded: true,
    isInstallPromptDismissed: false,
  })
);
const store = new Store({ name: 'ui', state, config });
persistState(store, {
  storage: localStorageStrategy,
  source: (store) =>
    store.pipe(
      select((state) => ({
        ...state,
        isSessionOver: false,
        isMobileDrawerOpen: false,
      }))
    ),
});

@Injectable({ providedIn: 'root' })
export class UiRepository {
  isDarkMode$ = store.pipe(select((state) => state.darkMode));
  isSessionOver$ = store.pipe(select((state) => state.isSessionOver));
  isMobileDrawerOpen$ = store.pipe(select((state) => state.isMobileDrawerOpen));
  isSidebarExpanded$ = store.pipe(select((state) => state.isSidebarExpanded));
  isInstallPromptDismissed$ = store.pipe(
    select((state) => state.isInstallPromptDismissed)
  );

  dismissInstallPrompt() {
    store.update((state) => ({
      ...state,
      isInstallPromptDismissed: true,
    }));
  }

  toggleDarkMode() {
    store.update((state) => ({
      ...state,
      darkMode: !state.darkMode,
    }));
  }

  setSessionOver() {
    store.update((state) => ({
      ...state,
      isSessionOver: true,
    }));
  }

  toggleMobileDrawer(isOpen?: boolean) {
    store.update((state) => ({
      ...state,
      isMobileDrawerOpen:
        isOpen !== undefined ? isOpen : !state.isMobileDrawerOpen,
    }));
  }

  toggleSidebarExpanded(isOpen?: boolean) {
    store.update((state) => ({
      ...state,
      isSidebarExpanded:
        isOpen !== undefined ? isOpen : !state.isSidebarExpanded,
    }));
  }
}
